import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../assets/img/logo.webp';
import API from '../../api/api';
import AuthWelcomeShell from './AuthWelcomeShell';
import shellStyles from './AuthWelcomeShell.module.css';

interface ResetPasswordFormProps {
  onResetSuccess?: (userCode: string) => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = () => {
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [token, setToken] = useState<Record<string, unknown> | null>(null);

  function parseJwt(jwt: string) {
    const base64Url = jwt.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  }

  useEffect(() => {
    if (tokenParam) {
      try {
        const data = parseJwt(tokenParam);
        setToken(data);
      } catch {
        setError('Invalid or expired reset link. Please request a new one.');
      }
    }
  }, [tokenParam]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError(null);
    setSuccess(false);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setError('Both password fields are required.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (token && typeof token.uid === 'string') {
        const { uid } = token;
        await API.post('/auth/reset', { password, uid });
        setSuccess(true);
        setPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError('Invalid or expired reset link. Please request a new password reset.');
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || 'An error occurred while resetting your password.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthWelcomeShell
      pageLabel="New password"
      leftHeading="Choose a strong password"
      leftDescription="Pick something memorable only to you. You'll be signed in on the next step."
    >
      <div className={shellStyles.logoBlock}>
        <Link to="/" aria-label="Home">
          <img src={logo} className={shellStyles.logoImg} alt="iGiTREE logo" />
        </Link>
      </div>
      <h1 className={shellStyles.title}>Set a new password</h1>
      <p className={shellStyles.subtitle}>
        Enter and confirm your new password below.
      </p>

      <form className="main-input" onSubmit={handleSubmit}>
        {success && (
          <div className="alert alert-success mb-3" role="alert">
            Your password has been reset successfully. Redirecting to sign in…
          </div>
        )}
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="input-group">
          <span className="input-group-text">
            <i className="fa-sharp fa-light fa-lock" aria-hidden />
          </span>
          <div className="form-floating">
            <input
              type="password"
              className="form-control"
              name="password"
              id="password"
              placeholder="New Password"
              value={password}
              onChange={handlePasswordChange}
              required
              autoComplete="new-password"
            />
            <label htmlFor="password">New Password</label>
          </div>
        </div>

        <div className="input-group">
          <span className="input-group-text">
            <i className="fa-sharp fa-light fa-lock" aria-hidden />
          </span>
          <div className="form-floating">
            <input
              type="password"
              className="form-control"
              name="confirmPassword"
              id="confirmPassword"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
              autoComplete="new-password"
            />
            <label htmlFor="confirmPassword">Confirm Password</label>
          </div>
        </div>

        <button type="submit" className={shellStyles.btnGradient} disabled={loading}>
          {loading ? 'Resetting...' : 'Reset password'}
        </button>
      </form>

      <div className={shellStyles.linkRow}>
        <Link to="/login" className={shellStyles.link}>
          Back to sign in
        </Link>
      </div>
    </AuthWelcomeShell>
  );
};

export default ResetPasswordForm;
